<template>
	<div class="component-section" @click="handleClick">
		<h3 class="section-title">Data Table</h3>
		<div class="table-content">
			<DataTable
				:value="sampleData"
				responsiveLayout="scroll"
				:row-hover="true"
				:paginator="true"
				:scrollable="false"
				:totalRecords="sampleData.length"
				:rows="5"
				v-model:first="firstRowOfPage"
				:page="0"
				paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
				:rowsPerPageOptions="[5, 10, 20]"
				currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
				dataKey="id"
				filterDisplay="menu"
				:loading="loading"
				:globalFilterFields="['name', 'category', 'material']"
				tableStyle="min-width: 50rem"
				selectionMode="single"
				v-model:selection="selectedRow"
			>
				<template #footer>
					<div class="flex gap-2 align-items-center">
						<i class="pi pi-plus-circle cursor-pointer"></i>
						<span class="cursor-pointer">Add Material</span>
						<i class="pi pi-plus-circle ml-4 cursor-pointer"></i>
						<span class="cursor-pointer">Add 5 Materials</span>
					</div>
				</template>

				<template #empty>
					<div class="text-center p-4">
						No materials found.
					</div>
				</template>
				
				<template #loading>
					<div style="text-align: center; padding: 1rem">
						<i class="pi pi-spinner pi-spin text-blue-500 mt-3 ml-2" style="font-size: 3rem"></i>
						<p>Loading data, please wait...</p>
					</div>
				</template>

				<Column 
					field="componentName" 
					filterField="componentName" 
					header="Component Name" 
					style="flex: 0 0 15%"
				>
					<template #body="{ data, index }">
						<InputText
							v-model="data.componentName"
							style="width: 180px; pointer-events: none;"
							:class="{
								'p-invalid': data.componentName == null,
								'': data.componentName !== null,
							}"
							readonly
						/>
					</template>
				</Column>

				<Column 
					field="materialCategory" 
					filterField="materialCategory" 
					header="Material Category" 
					style="flex: 0 0 20%"
				>
					<template #body="{ data, index }">
						<Select
							v-model="data.materialCategoryId"
							:options="materialCategories"
							optionLabel="name"
							option-value="id"
							placeholder="Select Category"
							class="w-full"
							style="pointer-events: none;"
							:class="{
								'p-invalid': data.materialCategoryId === undefined,
								'': data.materialCategoryId !== undefined,
							}"
						/>
					</template>
				</Column>

				<Column field="material" filterField="material" header="Material" style="flex: 0 0 30%">
					<template #body="{ data, index }">
						<div class="flex items-center gap-2">
							<Select
								v-model="data.materialId"
								:options="materials"
								optionLabel="name"
								option-value="id"
								placeholder="Select Material"
								class="w-full"
								style="pointer-events: none;"
								:class="{ 'p-invalid': data.materialId === null, '': data.materialId !== null }"
							/>
							<i class="pi pi-flask" v-show="data.isProxy"></i>
						</div>
					</template>
				</Column>

				<Column field="cas" filterField="cas" header="CAS" style="flex: 0 0 10%">
					<template #body="{ data, index }">
						<InputText
							v-model="data.cas"
							style="width: 120px; pointer-events: none;"
							placeholder="CAS Number"
							readonly
						/>
					</template>
				</Column>

				<Column field="quantity" filterField="quantity" header="Quantity" style="flex: 0 0 15%">
					<template #body="{ data, index }">
						<InputText
							v-model.number="data.quantity"
							style="width: 100px; pointer-events: none;"
							:class="{
								'p-invalid': data.quantity == null || data.quantity === '',
								'': data.quantity !== null && data.quantity !== '',
							}"
							readonly
						/>
					</template>
				</Column>

				<Column field="unit" filterField="unit" header="Unit" style="flex: 0 0 10%">
					<template #body="{ data, index }">
						<Select
							v-model="data.unitId"
							:options="units"
							optionLabel="symbol"
							option-value="id"
							placeholder="Unit"
							class="w-full"
							style="pointer-events: none;"
							:class="{
								'p-invalid': data.unitId === null,
								'': data.unitId !== null,
							}"
						/>
					</template>
				</Column>

				<Column header="Actions" style="flex: 0 0 10%">
					<template #body="{ data, index }">
						<div class="flex gap-1 justify-center">
							<Button 
								icon="pi pi-pencil" 
								size="small" 
								title="Edit"
								style="pointer-events: none;"
							/>
							<Button 
								icon="pi pi-trash" 
								size="small" 
								severity="danger" 
								title="Delete"
								style="pointer-events: none;"
							/>
						</div>
					</template>
				</Column>
			</DataTable>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import DataTable from "primevue/datatable"
import Column from "primevue/column"
import InputText from "primevue/inputtext"
import Select from "primevue/select"
import Button from "primevue/button"
// FontAwesome icons replaced with PrimeIcons for compatibility

const emit = defineEmits<{
	(e: "elementClick", event: MouseEvent): void
}>()

// Table state
const firstRowOfPage = ref(0)
const selectedRow = ref(null)
const loading = ref(false)

// Sample data with structure similar to assessmentTabRawMaterialsv2.vue
const sampleData = ref([
	{ 
		id: 1, 
		componentName: "Primary Container", 
		materialCategoryId: 1, 
		materialId: 1, 
		cas: "9003-07-0", 
		quantity: 100, 
		unitId: 1, 
		isProxy: false 
	},
	{ 
		id: 2, 
		componentName: "Secondary Packaging", 
		materialCategoryId: 2, 
		materialId: 2, 
		cas: "9002-88-4", 
		quantity: 250, 
		unitId: 2, 
		isProxy: true 
	},
	{ 
		id: 3, 
		componentName: "Label Material", 
		materialCategoryId: 3, 
		materialId: 3, 
		cas: "25249-16-5", 
		quantity: 50, 
		unitId: 1, 
		isProxy: false 
	},
	{ 
		id: 4, 
		componentName: "Adhesive", 
		materialCategoryId: 1, 
		materialId: 4, 
		cas: "25133-97-5", 
		quantity: 15, 
		unitId: 3, 
		isProxy: false 
	},
	{ 
		id: 5, 
		componentName: "Closure Material", 
		materialCategoryId: 2, 
		materialId: 5, 
		cas: "9003-53-6", 
		quantity: 75, 
		unitId: 1, 
		isProxy: false 
	},
	{ 
		id: 6, 
		componentName: "Buffer Solution", 
		materialCategoryId: 4, 
		materialId: 6, 
		cas: "7664-38-2", 
		quantity: 500, 
		unitId: 4, 
		isProxy: true 
	},
])

// Dropdown options (sample data)
const materialCategories = ref([
	{ id: 1, name: "Polymers" },
	{ id: 2, name: "Metals" },
	{ id: 3, name: "Paper & Cardboard" },
	{ id: 4, name: "Chemicals" }
])

const materials = ref([
	{ id: 1, name: "Polyethylene (PE)" },
	{ id: 2, name: "Polypropylene (PP)" },
	{ id: 3, name: "Paper Label Stock" },
	{ id: 4, name: "Acrylic Adhesive" },
	{ id: 5, name: "Aluminum Foil" },
	{ id: 6, name: "Phosphoric Acid" }
])

const units = ref([
	{ id: 1, symbol: "g" },
	{ id: 2, symbol: "kg" },
	{ id: 3, symbol: "mg" },
	{ id: 4, symbol: "ml" },
	{ id: 5, symbol: "l" }
])

// Event handlers
const updateData = (field: string, event: any, data: any) => {
	console.log(`Updated ${field} for item ${data.id}:`, event.target?.value || event)
	// In a real application, this would update the backend
}

const editItem = (data: any, index: number) => {
	console.log(`Edit item ${data.id}:`, data)
	// In a real application, this would open an edit dialog
}

const deleteItem = (data: any, index: number) => {
	console.log(`Delete item ${data.id}:`, data)
	// In a real application, this would show a confirmation dialog
}

const onlyNumbers = (event: KeyboardEvent) => {
	const charCode = event.which ? event.which : event.keyCode
	if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode !== 46) {
		event.preventDefault()
	}
}

const handleClick = (event: MouseEvent) => {
	emit("elementClick", event)
}
</script>

<style lang="scss" scoped>
.component-section {
	background: white;
	border-radius: 8px;
	padding: 1.5rem;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
	margin-bottom: 1rem;
}

.section-title {
	font-size: 1.2rem;
	font-weight: 600;
	margin-bottom: 1rem;
	color: #374151;
	border-bottom: 2px solid #e5e7eb;
	padding-bottom: 0.5rem;
}

.table-content {
	width: 100%;
	overflow-x: auto;
}

.flex {
	display: flex;
}

.gap-1 {
	gap: 0.25rem;
}

.gap-2 {
	gap: 0.5rem;
}

.items-center {
	align-items: center;
}

.justify-center {
	justify-content: center;
}

.align-items-center {
	align-items: center;
}

.cursor-pointer {
	cursor: pointer;
}

.ml-2 {
	margin-left: 0.5rem;
}

.ml-4 {
	margin-left: 1rem;
}

.mt-3 {
	margin-top: 0.75rem;
}

.text-center {
	text-align: center;
}

.text-blue-500 {
	color: #3b82f6;
}

.p-4 {
	padding: 1rem;
}

.w-full {
	width: 100%;
}

// DataTable specific styling using semantic values from database
:deep(.p-datatable-thead > tr > th) {
	background-color: var(--p-datatable-header-cell-background, #f8f9fa);
	color: var(--p-datatable-header-cell-color, #495057);
	border: 1px solid var(--p-datatable-header-cell-border-color, #dee2e6);
	padding: 12px 8px;
	font-weight: 600;
	font-size: 13px;
}

:deep(.p-datatable-tbody > tr > td) {
	background-color: var(--p-datatable-body-cell-background, #ffffff);
	color: var(--p-datatable-body-cell-color, #495057);
	border: 1px solid var(--p-datatable-body-cell-border-color, #dee2e6);
	padding: 10px 8px;
	font-size: 13px;
	vertical-align: middle;
}

:deep(.p-datatable-tbody > tr:hover) {
	background-color: var(--p-datatable-row-hover-background, #f8f9fa);
}

:deep(.p-datatable-tbody > tr.p-highlight) {
	background-color: var(--p-datatable-row-selected-background, #e3f2fd);
}

:deep(.p-paginator) {
	background: var(--p-surface-ground);
	border: 1px solid var(--p-content-border-color);
	border-radius: var(--p-border-radius);
}

:deep(.p-inputtext) {
	font-size: 13px;
}

:deep(.p-select) {
	font-size: 13px;
}

:deep(.p-datatable-footer) {
	background: var(--p-surface-ground);
	border: 1px solid var(--p-content-border-color);
	padding: 1rem;
}

// Ensure cells are clickable for color picker
:deep(td) {
	cursor: pointer !important;
}
</style>